<script setup>
/**
 * The member's notes, listed for "Add this to one of them, or save it as a new
 * note?" — Kapil's copy, 2026-09-23 ("I like this exactly").
 *
 *   1. Thomas Nellen - General Manager · Contact · Private Only · 3m ago
 *   2. Great energy and ambiance - patio is amazing too… · Highlight shared with your Network · 22h ago
 *   3. the booths along the patio are the ones to ask for… · Private Only · 5d ago
 *
 * Newest first, whatever kind — "chronological order… not saved people after".
 *
 * The server decides WHICH notes and in what order — the numbers are the ones
 * its chips ("Add to 2") mean — and this component only draws them. A note is
 * cut short to keep the list scannable on a phone; tapping it shows the whole
 * thing ("with the option to tap on the note and then see the full note").
 */
import { computed, onMounted, ref } from 'vue'
import axios from '@axios'
import { noteSnippet } from '@/utils/profileNotes'
import { entryAgo, isContactEntry, ownWords as ownWordsOf, statusLine } from '@/utils/noteDisplay'
import ProfileEntryBody from '@core/components/ProfileEntryBody.vue'

const props = defineProps({
  restaurantId: { type: String, required: true },
  entryIds: { type: Array, required: true },
})

const SHORT = 56

const rows = ref([])
const loading = ref(true)
const failed = ref(false)
const open = ref({})

const restaurantName = computed(() => rows.value.find(e => e.restaurant)?.restaurant?.name || '')

const isContact = isContactEntry

// Whose words, and what the status says — decided once, in utils/noteDisplay.
const ownWords = e => ownWordsOf(e, restaurantName.value)

function person(e) {
  const c = e.contact || {}

  return [c.cardName || c.name, c.role].filter(Boolean).join(' - ')
}

function short(e) {
  return noteSnippet(isContact(e) ? person(e) : ownWords(e), SHORT)
}

const status = e => statusLine(e, restaurantName.value)

const ago = e => entryAgo(e)

function toggle(id) {
  open.value = { ...open.value, [id]: !open.value[id] }
}

onMounted(async () => {
  try {
    const { data } = await axios.get('/profile/entries', { params: { restaurantId: props.restaurantId, limit: 50 } })
    const byId = new Map((Array.isArray(data) ? data : []).map(e => [String(e.entryId), e]))

    // The server's order, so "1" here is the "Add to 1" chip.
    rows.value = props.entryIds.map(id => byId.get(String(id))).filter(Boolean)
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <ol
    v-if="!loading && !failed && rows.length"
    class="note-choice"
  >
    <li
      v-for="(e, i) in rows"
      :key="e.entryId"
      class="note-choice__item"
    >
      <button
        type="button"
        class="note-choice__row"
        :aria-expanded="open[e.entryId] ? 'true' : 'false'"
        @click="toggle(e.entryId)"
      >
        <span class="note-choice__num">{{ i + 1 }}.</span>
        <span class="note-choice__line">
          <span class="note-choice__words">{{ short(e) }}</span>
          <span class="note-choice__meta"> · {{ status(e) }} · {{ ago(e) }}</span>
        </span>
      </button>

      <!-- The whole note, on tap. -->
      <div
        v-if="open[e.entryId]"
        class="note-choice__full"
      >
        <ProfileEntryBody
          :entry="e"
          :restaurant-name="restaurantName"
          text-class="note-choice__text"
        />
      </div>
    </li>
  </ol>
  <p
    v-else-if="loading"
    class="note-choice__loading"
  >
    Loading your notes…
  </p>
  <p
    v-else-if="failed"
    class="note-choice__loading"
  >
    Your notes could not be loaded — you can still choose below.
  </p>
</template>

<style scoped>
.note-choice {
  list-style: none;
  margin: 8px 0 4px;
  padding: 0;
}

.note-choice__item + .note-choice__item {
  margin-top: 4px;
}

/* A row reads as a line of the message, and tapping it opens the note. */
.note-choice__row {
  display: flex;
  gap: 6px;
  width: 100%;
  padding: 4px 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-size: 14px;
  line-height: 1.45;
  text-align: left;
  cursor: pointer;
}

.note-choice__row:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 4px;
}

.note-choice__num {
  flex: 0 0 auto;
  font-weight: 600;
}

.note-choice__meta {
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.note-choice__full {
  margin: 2px 0 6px 20px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.note-choice__text {
  margin: 0;
  font-size: 14px;
  white-space: pre-line;
}

.note-choice__loading {
  margin: 8px 0 4px;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}
</style>
